// src/libraryChat.js
// ═══════════════════════════════════════════════════════════════
// ASK THE LIBRARY — ONE conversation per person, shared by every chat UI
// ═══════════════════════════════════════════════════════════════
// The Lender Library page (src/views/LenderLibrary.jsx, Ask tab) and the
// floating chat bubble (src/views/ChatDock.jsx) are two windows onto the SAME
// thread. Both render it with the shared components in
// src/views/ChatThread.jsx; neither keeps a copy of its own.
//
// The thread lives here, in a small external store (one per staff login —
// useSyncExternalStore-compatible: subscribe / getSnapshot + useLibraryChat).
// A running answer streams into the STORE, at module level, so it keeps
// going whichever UI is mounted, hidden or minimized, and both UIs show it
// live. Only resetLibraryChats() (sign-out / idle sign-out) stops it.
//
// Store state (getSnapshot):
//   turns    the thread — questions, answers, failed answers (see normTurn)
//   live     null, or the answer being written: {status[], queries[], text,
//            premium, wantSources} — updated at most once per frame
//   busy     !!live
//   draft    the half-typed question (shared: type in one box, see it in the other)
//   filters  {lenders[], includeSuperseded} — the Library page's lender chips
//   fresh    {id, seen} — an answer that landed while NO chat UI was showing
//            the thread (seen: false, then "library" / "dock" once looked at).
//            Drives the bubble's dot and the Library's "finished while you
//            were away" tag + scroll.
// Actions: ask(question, {wantSources, premium, lenders, filters, context,
//          digOf, history}), stop(), digDeeper(turnId), retry(turnId),
//          toggleSources(turnId), newChat(), setDraft, setFilters,
//          setViewing(viewerId, on), markSeen(by).
//
// Persistence (localStorage, try/catch everywhere): "lf_kb_chat_v1:<staff>"
// — {v:1, filters, turns}; final answers only, never the streaming text.
// Migrated ONCE per person per device, the first time their key is missing:
// the old one-per-device Library thread ("lf_kb_chat_v1") becomes REECE's
// (never Max's), and the bubble's old per-person thread
// ("lf_kb_dock_chat_v1:<staff>") is merged in by exchange time, deduped by
// turn id; the old keys are removed only after the merged thread saved whole.
//
// Practice mode (Max only — src/access.js): NOTHING is written. The thread
// lives in memory only (no saveThread, no key migration), and flipping the
// switch either way starts that person's store over from what is saved on
// this device — a practice conversation is never written later, and never
// mixes into the real one.
//
// Deal-basics tag (bottom of this file): App reports the open loan file, and
// the New Call console can report its card (useReportDealContext). The
// composers show it as a removable chip — "Using: TX · 72 · $650K · HECM
// refi ✕" — and send it with a question ONLY while the chip is shown. Never
// a name, phone, email, street address or id: cleanDealContext (src/kb.js)
// keeps only state / age / value / balance / loan type / deal type / product
// / stage, and the server validates it again.

import { useEffect, useSyncExternalStore } from 'react';
import { askLibrary, cleanDealContext, DEAL_STATE_CODES, KB_LENDERS } from './kb';
import { ageFromDobStr, loanAge, isHs2File } from './lib';
import { isPractice, subscribeStaffPrefs } from './access';

export const CHAT_KEY = "lf_kb_chat_v1";            // + ":" + staff (the old bare key = Reece's, migrated)
export const DOCK_KEY = "lf_kb_dock_chat_v1";       // the bubble's old per-person key (migrated, then removed)
export const MAX_TURNS = 40;                        // messages kept in storage
const HISTORY_ITEMS = 12;                           // prior messages sent as context (server cap)
const HISTORY_CHARS = 6000;                         // per history message (server cap)
export const Q_MAX = 4000;                          // question length (server cap)
// The cost of each tier, said once for both UIs (Reece, 10/1: everyday ≈ 4–7¢).
export const DIG_TITLE = "Ask this again with the stronger model (Claude Opus) reading twice as many pages and every matching rate-sheet page in full — worth it for a tricky or multi-lender question. About 15–20¢, instead of about 4–7¢ for an everyday answer.";

// ═══════ pure helpers ═══════
const uid = function () { return Date.now().toString(36) + Math.random().toString(36).slice(2, 8); };
const lenderList = function () { return Array.isArray(KB_LENDERS) ? KB_LENDERS : []; };
export const cleanCites = function (c) { const out = []; (Array.isArray(c) ? c : []).forEach(function (x) { const n = Number(x); if (n > 0 && out.indexOf(n) < 0) out.push(n); }); return out.sort(function (a, b) { return a - b; }); };
export const costOf = function (v) { const n = Number(v); return v != null && v !== "" && Number.isFinite(n) && n >= 0 ? n : null; };
// "2026-09-29" → "9/29/26" (string math — no Date(), so no timezone drift)
export const fmtEff = function (d) { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || "")); return m ? (+m[2]) + "/" + (+m[3]) + "/" + m[1].slice(2) : ""; };
export const sourceLabel = function (s) { return (s.lender ? s.lender + " — " : "") + (s.title || "Untitled") + " · p." + s.page + (s.effectiveDate ? " · eff. " + fmtEff(s.effectiveDate) : ""); };
// An answer's estimated API cost (the server's cost.usd) → "≈ 4¢". partial:
// the answer was cut off before the final token count came back, so the
// server's figure is a floor → "≥ 4¢".
export function fmtCost(usd, partial) {
  const n = Number(usd);
  if (usd == null || !Number.isFinite(n) || n < 0) return "";
  const c = n * 100;
  const pre = partial ? "≥ " : "≈ ";
  if (c < 0.1) return partial ? pre + c.toFixed(2) + "¢" : "< 0.1¢";
  if (c < 0.95) return pre + c.toFixed(1) + "¢";
  if (c < 99.5) return pre + Math.round(c) + "¢";
  return pre + "$" + n.toFixed(2);
}
// The search filters a question was sent with ({lenders, includeSuperseded}),
// kept on its turn so a "Dig deeper" (or its retry) asks the same thing —
// not whatever the chips say by then. null when absent (turns saved before).
export function normFilters(f) {
  if (!f || typeof f !== "object" || Array.isArray(f)) return null;
  return { lenders: Array.isArray(f.lenders) ? f.lenders.filter(function (l) { return typeof l === "string" && l.trim(); }).slice(0, 20) : [], includeSuperseded: f.includeSuperseded === true };
}
// The chips themselves: only lenders the Library knows.
function normChips(f) {
  const o = f && typeof f === "object" && !Array.isArray(f) ? f : {};
  const known = lenderList();
  return { lenders: Array.isArray(o.lenders) ? o.lenders.filter(function (l, i, a) { return known.indexOf(l) >= 0 && a.indexOf(l) === i; }) : [], includeSuperseded: o.includeSuperseded === true };
}
export function normSource(s) {
  return { n: Number(s.n), docId: String(s.docId), page: Math.max(1, parseInt(s.page, 10) || 1), title: String(s.title || "Untitled"),
    lender: String(s.lender || ""), docType: String(s.docType || ""), effectiveDate: s.effectiveDate || null, superseded: !!s.superseded, citedText: String(s.citedText || "") };
}
// A question (user turn) or a failed answer (error turn) also keeps what it
// was sent with — the filters, the deal-basics context, and on a deeper
// review, digOf (the id of the answer it digs into) — so a retry re-sends the
// same request. All absent on turns saved before they existed.
function withAsked(out, t) {
  const f = normFilters(t.filters);
  if (f) out.filters = f;
  if (out.premium && typeof t.digOf === "string" && t.digOf) out.digOf = t.digOf;
  const c = cleanDealContext(t.context);
  if (c) out.context = c;
  return out;
}
export function normTurn(t) {
  if (!t || typeof t !== "object" || typeof t.id !== "string") return null;
  const at = Number(t.at) || 0;
  if (t.role === "user") return typeof t.text === "string" ? withAsked({ id: t.id, role: "user", text: t.text, premium: t.premium === true, at: at }, t) : null;
  if (t.role !== "assistant") return null;
  if (t.error) return withAsked({ id: t.id, role: "assistant", error: { code: String(t.error.code || ""), message: String(t.error.message || "Something went wrong."), detail: String(t.error.detail || "") }, question: typeof t.question === "string" ? t.question : "", premium: t.premium === true, at: at }, t);
  const blocks = (Array.isArray(t.blocks) ? t.blocks : []).filter(function (b) { return b && typeof b.text === "string"; }).map(function (b) { return { text: b.text, cites: cleanCites(b.cites) }; });
  const sources = (Array.isArray(t.sources) ? t.sources : []).filter(function (s) { return s && s.docId && s.n != null; }).map(normSource);
  // Turns saved before showSources existed load with their Sources closed;
  // before cost/premium, with no cost label and premium null (no "Dig deeper").
  return { id: t.id, role: "assistant", blocks: blocks, sources: sources, searched: t.searched || null, queries: Array.isArray(t.queries) ? t.queries.map(String) : [], stopped: !!t.stopped, incomplete: !!t.incomplete, showSources: t.showSources === true,
    cost: costOf(t.cost), costPartial: t.costPartial === true, premium: typeof t.premium === "boolean" ? t.premium : null, at: at };
}
export function answerText(t) { return ((t && t.blocks) || []).map(function (b) { return b.text; }).join(""); }
// Plain text of an answer. withCites adds [n] markers + a Sources list (Copy).
export function answerPlain(t, withCites) {
  let s = ((t && t.blocks) || []).map(function (b) {
    if (!withCites || !b.cites.length) return b.text;
    const m = /\s*$/.exec(b.text); const tail = m ? m[0] : "";
    return b.text.slice(0, b.text.length - tail.length) + b.cites.map(function (n) { return "[" + n + "]"; }).join("") + tail;
  }).join("");
  if (withCites && t.sources && t.sources.length) {
    s = s.replace(/\s+$/, "") + "\n\nSources:\n" + t.sources.map(function (x) { return "[" + x.n + "] " + sourceLabel(x) + (x.superseded ? " (superseded)" : ""); }).join("\n");
  }
  return s;
}
// Prior turns for the server: completed question/answer pairs only (so roles
// always alternate), answer text without [n] markers, last HISTORY_ITEMS.
export function buildHistory(turns) {
  const out = [];
  const list = Array.isArray(turns) ? turns : [];
  const clip = function (s) { s = String(s || ""); return s.length > HISTORY_CHARS ? s.slice(0, HISTORY_CHARS) : s; };
  for (let i = 0; i < list.length - 1; i++) {
    const u = list[i], a = list[i + 1];
    if (u.role === "user" && a.role === "assistant" && !a.error && !a.stopped && a.blocks && a.blocks.length) {
      out.push({ role: "user", text: clip(u.text) }, { role: "assistant", text: clip(answerPlain(a, false)) });
      i++;
    }
  }
  return out.slice(-HISTORY_ITEMS);
}
// History for a deeper review: the thread as it stood BEFORE the question it
// digs into, so the stronger model doesn't read its own quick answer as
// settled. That's the answer `digOf`'s question; failing that (a turn saved
// before digOf existed, or that answer was trimmed off the thread), the last
// everyday asking of the same question; else the whole thread.
export function digHistory(turns, digOf, q) {
  const list = Array.isArray(turns) ? turns : [];
  const i = digOf ? list.findIndex(function (x) { return x.id === digOf; }) : -1;
  if (i >= 1 && list[i - 1].role === "user") return buildHistory(list.slice(0, i - 1));
  for (let j = list.length - 1; j >= 0; j--) {
    const u = list[j];
    if (u.role === "user" && !u.premium && String(u.text || "").trim() === q) return buildHistory(list.slice(0, j));
  }
  return buildHistory(list);
}
// The askLibrary `final` event (+ what streamed) → a saved answer turn.
export function finalToTurn(fin, L) {
  const a = fin && fin.answer;
  let blocks = [];
  if (a && Array.isArray(a.blocks)) blocks = a.blocks.map(function (b) { return { text: String((b && b.text) || ""), cites: cleanCites(b && b.cites) }; }).filter(function (b) { return b.text || b.cites.length; });
  else if (typeof a === "string") blocks = [{ text: a, cites: [] }];
  else if (a && typeof a.text === "string") blocks = [{ text: a.text, cites: [] }];
  const incomplete = !fin;
  if (!blocks.length && L && L.text) blocks = [{ text: L.text, cites: [] }];
  const sources = (fin && Array.isArray(fin.sources) ? fin.sources : []).filter(function (s) { return s && s.n != null && s.docId; }).map(normSource);
  // cost: the server's estimate for this answer (USD); premium: answered by
  // "Dig deeper" — the server says which, else what was asked for.
  const c = fin && fin.cost && typeof fin.cost === "object" ? fin.cost : null;
  const premium = c && typeof c.premium === "boolean" ? c.premium : !!(L && L.premium);
  // showSources: open from the start when the question asked where it comes
  // from; after that, whatever was last picked (toggleSources).
  return { id: uid(), role: "assistant", blocks: blocks, sources: sources, searched: (fin && fin.searched) || null, queries: (L && L.queries) || [], incomplete: incomplete, showSources: !!(L && L.wantSources),
    cost: c ? costOf(c.usd) : null, costPartial: !!(c && c.partial === true), premium: premium, at: Date.now() };
}

// ── Errors → plain English ──
// kb.js already turns HTTP / network / database / storage failures into
// sentences he can act on, so those are shown as-is. Only the server's
// in-stream error codes get our wording; its own text rides along as detail.
const ASK_ERR = {
  no_library: "Your library doesn't have any ready documents yet. Upload guidelines, bulletins or rate sheets in the Lender Library, then ask again.",
  refusal: "Claude declined to answer that one. Try rephrasing the question.",
  upstream_error: "The AI service had a problem putting the answer together. Wait a moment and try again.",
  db_error: "Couldn't search your library database. Try again — if it keeps happening, the Lender Library SQL may not have been run in Supabase yet.",
  bad_request: "That question couldn't be sent — it may be empty or too long (4,000 characters max).",
};
const ASK_FALLBACK = "Something went wrong getting an answer. Try again.";
export function askError(errEvt, e) {
  if (errEvt) {
    const code = String(errEvt.code || "");
    const raw = String(errEvt.message || "").trim();
    const message = ASK_ERR[code] || raw || ASK_FALLBACK;
    return { code: code, message: message, detail: raw && raw !== message ? raw : "" };
  }
  return { code: String((e && e.code) || ""), message: (e && e.message) || ASK_FALLBACK, detail: "" };
}
// Retrying a question or a failed answer: a deeper review is re-sent as it
// was — the same answer it digs into (so the same history), the same filters
// and the same deal; an everyday question uses the chips as they are now but
// keeps the deal it was asked about.
export function retryOpts(t) {
  const ctx = (t && t.context) || null;
  return t && t.premium === true ? { premium: true, digOf: t.digOf, filters: t.filters, context: ctx } : { premium: false, context: ctx };
}

// ── "Where does it say that?" ──
// Does the question ask WHERE the answer comes from? Then the server is told
// to name documents and pages (wantSources) and the answer opens with its
// Sources showing. Conservative on purpose: a miss costs one tap on
// "Sources", a false hit stuffs page numbers into a quick answer. Loan talk
// that happens to use these words is not a request for sources: "source of
// funds", "can a gift be the source?", "reference rate", "credit references",
// "can I quote that?" (pricing), "the appraiser cited…", "does the appraiser
// need to cite comps?", "what documents show that the borrower lives there?",
// "what documents does it need?", "exact language naming the borrowers",
// "where does the borrower say…". When unsure, it does not trigger.
// Regex pieces. Built from strings so the word lists are written once.
const SRC_RE = function (s) { return new RegExp(s); };
const SRC_END = " *[?.!]*$";                                                            // …and nothing after it
const SRC_LEAD = "(^|[,.;:!?] )";                                                       // start of the question (or of a sentence in it)
const SRC_IMP = "(^|[,.;:!?] |\\b(you|please|pls|just|and|also|then|now) )";            // "give me…" / "can you give me…" — not "the borrower has to give…"
const SRC_PRON = "(that|this|it|those|these)";
const SRC_DOCS = "(guidelines?|handbook|bulletins?|matrix|rate ?sheets?|manuals?|ml|mortgagee letters?|program guides?|library|4000\\.1)"; // library documents (never "docs" — closing docs are loan talk)
const SRC_PART = "(guidelines?|handbook|bulletins?|sections?|pages?|pg|matrix|rate ?sheets?|manuals?|ml|mortgagee letters?|program guides?|sources?)";
const SRC_ANYDOC = "(documents?|docs?|" + SRC_PART.slice(1);                              // SRC_PART + document/doc
const SRC_WHO = "(hud|fha|foa|finance of america|longbridge|smartfi|omaha)";
const SRC_SAY = "(says?|states?|mentions?)";
// Always a request, whatever else the question says:
const SRC_ASK = [
  // where does it / HUD / the guideline say … — not "where does the borrower say", "where do I say"
  SRC_RE("\\bwhere (does|did|do|is|was) (it|that|this|hud|fha) " + SRC_SAY + "\\b"),
  SRC_RE("\\bwhere (does|did|do|is|was) [^?.!,;\\n]{0,30}?\\b(" + SRC_DOCS.slice(1, -1) + "|section|policy|" + SRC_WHO.slice(1, -1) + ")('s)? " + SRC_SAY + "\\b"),
  SRC_RE("\\bwhere (does|did|do|is|was) (they|the (docs?|documents?)) (says?|states?) (that|this|it|so)\\b"),
  SRC_RE("\\bwhere's (it|that|this) " + SRC_SAY + "\\b"),
  SRC_RE("\\bwhere (it|that|this|hud|fha) " + SRC_SAY + "\\b"),                           // show me / point me to where it says that
  SRC_RE("\\bwhere( is| was|'s) " + SRC_PRON + " (from|coming from|stated|written)\\b"),
  SRC_RE("\\bwhere( is| was|'s) " + SRC_PRON + " (in|on|under) [^?.!,;\\n]{0,30}?\\b(" + SRC_DOCS.slice(1, -1) + "|docs?|documents?|hud|fha)\\b"),
  SRC_RE("\\bwhere( does| did| do|'d) " + SRC_PRON + " come from\\b"),
  SRC_RE("\\bwhere( did|'d| are| were) you (get|getting|find|see|read|pull)\\b"),
  SRC_RE("\\bwhere (can|could|do|would|should) (i|we) (find|see|read|look up) " + SRC_PRON + "( (in|on|under) [^?.!,;\\n]{0,30}?\\b(" + SRC_DOCS.slice(1, -1) + "|docs?|documents?|hud|fha))?" + SRC_END),
  SRC_RE("\\bwhere in (the |hud |fha |[a-z]+'s )?[^?.!,;\\n]{0,20}?\\b(" + SRC_DOCS.slice(1, -1) + "|hud|fha)\\b"),
  // which page / document / guideline … — about the answer, not "what page does the NBS sign", "what documents does it need"
  SRC_RE("\\b(which|what) (pages?|pg)( numbers?)?" + SRC_END),
  SRC_RE("\\b(which|what) (pages?|pg) (is|was|are|were) " + SRC_PRON + "\\b"),
  SRC_RE("\\b(which|what) " + SRC_ANYDOC + " (is|was) " + SRC_PRON + "( (from|in|on|under)( [^?.!,;\\n]{0,30})?)?" + SRC_END),
  SRC_RE("\\b(which|what) " + SRC_PART + " (says?|said|states?|stated|mentions?|mentioned|covers?|has|had|shows?) (that|this|it|so)\\b"),
  SRC_RE("\\b(which|what) (documents?|docs?) (says?|said|states?|stated|mentions?|mentioned|covers?|has|had|shows?) (that|this|it|so|those|these)" + SRC_END),
  SRC_RE("^((ok|okay|so|and|but),? )?which (documents?|docs?)" + SRC_END),
  SRC_RE("\\bwhich " + SRC_PART + SRC_END),
  SRC_RE("\\b(from|in|on) (which|what) " + SRC_ANYDOC + "( (is|was) (that|this|it)( (from|in|on))?)?" + SRC_END),
  SRC_RE("\\b(show|pull up|bring up|open|link)( me| us)? (the|that|this|those|these) (pages?|guidelines?|bulletins?|sections?|passages?|language|wording|mortgagee letters?|ml|handbook)\\b"),
  SRC_RE("\\b(show|pull up|bring up|open|link)( me| us)? (that|this|those|these) (docs?|documents?)\\b"),
  SRC_RE("\\b(show|pull up|bring up|open|link)( me| us)? the (docs?|documents?)" + SRC_END),
  SRC_RE("\\bpage (numbers?\\b|#)|\\bpage#"),
  // exact wording / quotes — not "exact language naming the borrowers", "can I quote that?" (pricing)
  SRC_RE("\\b(exact|direct|actual|precise) (quotes?|wording|words|language|text)( (please|pls))?" + SRC_END),
  SRC_RE("\\b(exact|direct|actual|precise) (quotes?|wording|words|language|text) (from|in|of|on|for) (" + SRC_PRON.slice(1, -1) + "|the answer|hud|fha|the " + SRC_ANYDOC + "|the (source|library)|[a-z]+'s " + SRC_PART + ")\\b"),
  SRC_RE(SRC_IMP + "quote (me )?(the|that|this) (lines?|sections?|passages?|parts?|language|wording|text|guidelines?|pages?|rules?|sentences?|paragraphs?|bulletins?)\\b"),
  SRC_RE("\\b((can|could|would|will) you( please| pls| just)?|please|pls|just) quote (it|that|them)( for me)?( (please|pls))?" + SRC_END),
  SRC_RE("\\bquotes? (from|out of) (the |hud |fha |[a-z]+'s )?(" + SRC_DOCS.slice(1, -1) + "|docs?|documents?|pages?)\\b"),
  SRC_RE("^(verbatim|word for word)( (please|pls))?" + SRC_END),
  SRC_RE("\\b(say|give|tell|read|quote|show|paste)( me| us)?( it| that| this| them| the (lines?|sections?|passages?|text|language|wording))?( to me)? (verbatim|word for word)\\b"),
  // the source asked for outright — wins over the loan-word check below ("what is the source for that")
  SRC_RE("\\bwhat('s| is| was| are|'re| were|s) (the|your|its|their) (sources?|references?|citations?)( (for|of|on|behind) (" + SRC_PRON.slice(1, -1) + "|the answer|your answer|that answer))?" + SRC_END),
  SRC_RE("\\b(do|did) you have (a |an |any |the )?(sources?|references?|citations?|cites)( (for|on) " + SRC_PRON + ")?" + SRC_END),
  SRC_RE("\\b(is|are) there (a |an |any )?(sources?|references?|citations?)( (for|on|behind) " + SRC_PRON + ")?" + SRC_END),
  SRC_RE(SRC_LEAD + "(and |ok,? |okay,? |so )?(got|any|got any|you got|have you got) (a |an |any )?(sources?|references?|citations?|cites)( (for|on) " + SRC_PRON + ")?( (please|pls))?" + SRC_END),
];
// "source(s)" / "reference(s)" / "cite" asked for as such…
const SRC_WORD = [
  SRC_RE(SRC_LEAD + "((and|ok|okay|also|so|now|then|plus|the|your|any|some),? )*(sources?|references?|citations?|cites)( (please|pls))?" + SRC_END),   // "Sources?", "references please"
  SRC_RE("\\b(sources?|references?|citations?|cites) (please|pls)\\b"),
  SRC_RE("\\b(sources?|references?|citations?|cites) (of|for|on|behind) (" + SRC_PRON.slice(1, -1) + "|each|each one|all|all of (that|this|it|those|these|them)|the answer|your answer|that answer|this answer)( (please|pls))?" + SRC_END),
  SRC_RE(SRC_IMP + "(also )?(give|show|list|cite|include|add|provide|share|post|drop)( me| us)? (the |your |some |all |any |a |an |those |these )?(sources?|references?|citations?|cites)\\b"),
  SRC_RE("\\b(with|w/) (the |your |some |all )?(sources?|citations?|cites)\\b"),
  SRC_RE(SRC_IMP + "cite\\b"),                                                          // "can you cite that" — not "does the appraiser need to cite comps"
  SRC_RE("\\bcitations?\\b"),
  SRC_RE("\\b(i|we) (need|want|'d like|would like) (the |some |your )?(sources?|citations?|cites)\\b"),
  SRC_RE("\\b(can|could|may) (i|we) (get|have|see)( a| an| the| some| any| your)? (sources?|citations?|cites)\\b"),
];
// …unless the question also uses them as loan words.
const SRC_LOANWORD = [
  /\bsources? of (?!(that|this|it|those|these|the answer|your answer)\b)/,                                  // source of funds
  /\b(is|are|be|was|were|being|been|counts? as|counted as|considered|considered as|used as|uses? as|using as|qualify as|qualifies as|serves? as|acts? as)\b[^?.!]{0,40}?\b(a|an|the|my|their|his|her|our|its|one|another) (sources?|references?)\b/, // "can a gift be the source?"
  /\b(income|funding|funds?|assets?|repayment|payment|down ?payment|cash|money|revenue|retirement|pension|alternative|acceptable|eligible|allowable|other|primary|secondary|outside|third[- ]party|gift|qualifying|valid|approved|closing|credit|rental|landlord|character|personal|employment|trade|bank) (sources?|references?)\b/,
  /\b(code|city|county|building|zoning|traffic|health|safety|tax) citations?\b/,
  /\b(got|get|gets|getting|received|receives?|issued|issues?) (a |an |any |the |some |two |\d+ )?([a-z]+ )?citations?\b/,  // "got a citation from the city"
];
export function asksForSources(question) {
  const q = String(question || "").toLowerCase().replace(/[‘’]/g, "'").replace(/\s+/g, " ").trim();
  if (!q) return false;
  if (SRC_ASK.some(function (re) { return re.test(q); })) return true;
  return !SRC_LOANWORD.some(function (re) { return re.test(q); }) && SRC_WORD.some(function (re) { return re.test(q); });
}

// ═══════ persistence ═══════
export function chatKey(staff) { return CHAT_KEY + ":" + (staff || "reece"); }
const EMPTY_FILTERS = { lenders: [], includeSuperseded: false };
function readKey(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
function parseThread(raw) {
  try {
    const o = JSON.parse(raw);
    const list = Array.isArray(o) ? o : (o && Array.isArray(o.turns) ? o.turns : []);
    const f = o && !Array.isArray(o) && o.filters ? normChips(o.filters) : null;
    return { turns: list.map(normTurn).filter(Boolean), filters: f };
  } catch (e) { return null; }
}
// Two threads → one, by EXCHANGE (a question and the answers after it stay
// together, so the history sent to the server still pairs up), oldest
// exchange first, without duplicate turns.
export function mergeThreads(lists) {
  const units = [];
  (lists || []).forEach(function (list, li) {
    let cur = null;
    (Array.isArray(list) ? list : []).forEach(function (t, ti) {
      if (!t) return;
      if (t.role === "user" || !cur) { cur = { at: Number(t.at) || 0, li: li, ti: ti, turns: [] }; units.push(cur); }
      cur.turns.push(t);
    });
  });
  units.sort(function (a, b) { return (a.at - b.at) || (a.li - b.li) || (a.ti - b.ti); });
  const seen = new Set();
  const out = [];
  units.forEach(function (u) {
    u.turns.forEach(function (t) {
      const sig = t.role + "|" + (t.at || 0) + "|" + (t.role === "user" ? t.text : t.error ? "err:" + t.error.message : answerText(t));
      if (seen.has("id:" + t.id) || seen.has(sig)) return;
      seen.add("id:" + t.id); seen.add(sig);
      out.push(t);
    });
  });
  return out;
}
// How many turns made it to storage (fewer if it was full; -1 if nothing could be written).
export function saveThread(staff, turns, filters) {
  const list = Array.isArray(turns) ? turns : [];
  const tries = [MAX_TURNS, 10, 0];
  for (let i = 0; i < tries.length; i++) {
    const kept = tries[i] ? list.slice(-tries[i]) : [];
    try { localStorage.setItem(chatKey(staff), JSON.stringify({ v: 1, filters: filters || EMPTY_FILTERS, turns: kept })); return kept.length; } catch (e) { /* try smaller */ }
  }
  return -1;
}
// This person's saved thread on this device (migrating the old keys once).
export function loadThread(staff) {
  const who = staff || "reece";
  const empty = { turns: [], filters: EMPTY_FILTERS };
  const own = readKey(chatKey(who));
  if (own != null) {
    const p = parseThread(own);
    return p ? { turns: p.turns.slice(-MAX_TURNS), filters: p.filters || EMPTY_FILTERS } : empty;
  }
  const legacy = [];
  const parts = [];
  let filters = null;
  if (who === "reece") {
    const raw = readKey(CHAT_KEY);
    if (raw != null) { legacy.push(CHAT_KEY); const p = parseThread(raw); if (p) { parts.push(p.turns); filters = p.filters; } }
  }
  const dk = DOCK_KEY + ":" + who;
  const rawDock = readKey(dk);
  if (rawDock != null) { legacy.push(dk); const p = parseThread(rawDock); if (p) parts.push(p.turns); }
  if (!legacy.length) return empty;
  const out = { turns: mergeThreads(parts).slice(-MAX_TURNS), filters: filters || EMPTY_FILTERS };
  if (isPractice(who)) return out;   // practice: read only — the migration waits for a real session
  if (saveThread(who, out.turns, out.filters) === out.turns.length) {
    legacy.forEach(function (k) { try { localStorage.removeItem(k); } catch (e) { /* ignore */ } });
  }
  return out;
}

// ═══════ the store ═══════
const raf = function (f) {
  if (typeof requestAnimationFrame === "function") return { raf: requestAnimationFrame(f) };
  return { t: setTimeout(f, 16) };
};
const cancelRaf = function (h) {
  if (!h) return;
  if (h.raf != null && typeof cancelAnimationFrame === "function") cancelAnimationFrame(h.raf);
  if (h.t != null) clearTimeout(h.t);
};
const liveView = function (w) { return { status: w.status, queries: w.queries, text: w.text, premium: w.premium, wantSources: w.wantSources }; };

function createStore(staff) {
  const who = staff || "reece";
  const boot = loadThread(who);
  let state = { staff: who, turns: boot.turns, filters: boot.filters, draft: "", live: null, busy: false, fresh: null };
  const subs = new Set();
  const viewers = new Set();
  let ctrl = null;        // the running answer's AbortController
  let work = null;        // what has streamed so far (mutated per event; `live` is its per-frame copy)
  let frame = null;
  let gen = 0;            // bumps on New chat / sign-out so a late answer can't land in the new thread
  let disposed = false;

  const emit = function () { subs.forEach(function (f) { try { f(); } catch (e) { /* a view went away */ } }); };
  const set = function (patch, persist) {
    if (disposed) return;
    state = Object.assign({}, state, patch);
    if (persist && !isPractice(who)) saveThread(who, state.turns, state.filters);
    emit();
  };
  const stopFrame = function () { cancelRaf(frame); frame = null; };
  const pushLive = function (w) {
    if (frame || disposed) return;
    frame = raf(function () { frame = null; if (work === w && !disposed) set({ live: liveView(w) }); });
  };

  async function ask(text, opts) {
    const o = opts || {};
    const premium = o.premium === true;
    const q = String(text == null ? "" : text).trim();
    if (disposed || !q || ctrl || q.length > Q_MAX) return false;
    const myGen = gen;
    const turns = state.turns;
    const digOf = premium && typeof o.digOf === "string" && o.digOf ? o.digOf : null;
    const hist = Array.isArray(o.history) ? o.history : premium ? digHistory(turns, digOf, q) : buildHistory(turns);
    let sent = normFilters(o.filters);
    if (!sent) sent = { lenders: Array.isArray(o.lenders) ? normFilters({ lenders: o.lenders }).lenders : state.filters.lenders.slice(), includeSuperseded: !!state.filters.includeSuperseded };
    // "where does it say that?" → the answer names its pages, Sources open
    const wantSources = typeof o.wantSources === "boolean" ? o.wantSources : asksForSources(q);
    const context = cleanDealContext(o.context);
    const asked = premium ? (digOf ? { premium: true, digOf: digOf, filters: sent } : { premium: true, filters: sent }) : { filters: sent };
    if (context) asked.context = context;
    const c = new AbortController();
    ctrl = c;
    const w = { status: [], queries: [], text: "", wantSources: wantSources, premium: premium };
    work = w;
    set({ turns: turns.concat([Object.assign({ id: uid(), role: "user", text: q }, asked, { at: Date.now() })]), draft: premium ? state.draft : "", fresh: null, live: liveView(w), busy: true }, true);

    const f = { includeSuperseded: sent.includeSuperseded };
    // Omit lenders entirely when none picked, so the server can use the lenders it plans from the question.
    if (sent.lenders.length) f.lenders = sent.lenders.slice();
    const params = { question: q, history: hist, filters: f, wantSources: wantSources, premium: premium, signal: c.signal };
    if (context) params.context = context;
    let errEvt = null;
    let turn;
    try {
      const fin = await askLibrary(params, function (evt) {
        if (!evt || typeof evt !== "object" || work !== w) return;
        if (evt.type === "status" && evt.text) { const s = String(evt.text); if (w.status[w.status.length - 1] !== s) w.status = w.status.concat([s]); }
        else if (evt.type === "queries" && Array.isArray(evt.queries)) w.queries = evt.queries.map(String);
        else if (evt.type === "delta" && evt.text) w.text += String(evt.text);
        else if (evt.type === "error") { errEvt = evt; return; }
        else return;
        pushLive(w);
      });
      turn = finalToTurn(fin, w);
    } catch (e) {
      if (c.signal.aborted || (e && e.name === "AbortError")) {
        turn = { id: uid(), role: "assistant", stopped: true, blocks: w.text.trim() ? [{ text: w.text, cites: [] }] : [], sources: [], queries: w.queries || [], showSources: wantSources, cost: null, premium: premium, at: Date.now() };
      } else {
        turn = Object.assign({ id: uid(), role: "assistant", error: askError(errEvt, e), question: q, premium: premium }, asked, { at: Date.now() });
      }
    }
    if (ctrl === c) ctrl = null;
    if (work === w) { work = null; stopFrame(); }
    if (disposed) return false;
    const idle = ctrl === null;
    if (gen === myGen) {
      const patch = { turns: state.turns.concat([turn]) };
      if (idle) { patch.live = null; patch.busy = false; }
      // Landed while no chat UI showed the thread: the bubble gets a dot, the
      // Library a "finished while you were away" tag.
      if (!turn.stopped && viewers.size === 0) patch.fresh = { id: turn.id, seen: false };
      set(patch, true);
    } else if (idle) {
      set({ live: null, busy: false });
    }
    return true;
  }
  function stop() { if (ctrl) ctrl.abort(); }
  // "Dig deeper" on an answer: the question it answered (the user turn just
  // before it — an answer whose question was trimmed off the saved thread
  // has none), asked again with premium:true, the conversation as it stood
  // before that question (digOf → digHistory), and the filters + deal it was
  // sent with (a turn saved before those were kept: the chips as they are).
  function digDeeper(id) {
    const ts = state.turns;
    const i = ts.findIndex(function (x) { return x.id === id; });
    if (i < 1 || ts[i - 1].role !== "user") return false;
    const u = ts[i - 1];
    return ask(u.text, { premium: true, digOf: id, filters: u.filters, context: u.context || null });
  }
  // "Try again" on a failed answer, "Ask again" on a question that never got one.
  function retry(id) {
    const t = state.turns.find(function (x) { return x.id === id; });
    if (!t) return false;
    if (t.role === "user") return ask(t.text, retryOpts(t));
    if (t.role === "assistant" && t.error && t.question) return ask(t.question, retryOpts(t));
    return false;
  }
  // Open/close one answer's Sources; the choice is saved with the thread.
  function toggleSources(id) {
    set({ turns: state.turns.map(function (x) { return x.id === id ? Object.assign({}, x, { showSources: !x.showSources }) : x; }) }, true);
  }
  function newChat() {
    gen++;
    if (ctrl) { const c = ctrl; ctrl = null; c.abort(); }
    work = null; stopFrame();
    set({ turns: [], draft: "", fresh: null, live: null, busy: false }, true);
  }
  function setDraft(v) { const s = String(v == null ? "" : v); if (s !== state.draft) set({ draft: s }); }
  function setFilters(upd) {
    const next = normChips(typeof upd === "function" ? upd(state.filters) : upd);
    set({ filters: next }, true);
  }
  // Which chat UIs are showing the thread right now ("library", "dock").
  function setViewing(id, on) { if (on) viewers.add(id); else viewers.delete(id); }
  function markSeen(by) {
    const f = state.fresh;
    if (f && !f.seen) set({ fresh: { id: f.id, seen: by || true } });
  }
  function dispose() {
    disposed = true; gen++;
    if (ctrl) { const c = ctrl; ctrl = null; c.abort(); }
    work = null; stopFrame();
    subs.clear(); viewers.clear();
  }
  // Being replaced (Practice mode flipped): wake every view with a changed
  // snapshot so it re-reads getChatStore() and finds the new store, then go
  // inert (a running answer is stopped and can't land anywhere).
  function retire() {
    if (disposed) return;
    state = Object.assign({}, state, { live: null, busy: false, retired: true });
    emit();
    dispose();
  }
  const subscribe = function (fn) { subs.add(fn); return function () { subs.delete(fn); }; };
  const getSnapshot = function () { return state; };

  return { staff: who, subscribe, getSnapshot, ask, stop, digDeeper, retry, toggleSources, newChat, setDraft, setFilters, setViewing, markSeen, dispose, retire };
}

const STORES = new Map();
// The store for one staff login ("reece" | "max"), created on first use.
export function getChatStore(staff) {
  const who = staff || "reece";
  let s = STORES.get(who);
  if (!s) { s = createStore(who); STORES.set(who, s); }
  return s;
}
// Start one person's store over from what is saved on this device (Practice
// mode switched on or off). The next getChatStore() builds a fresh one.
export function restartChatStore(staff) {
  const who = staff || "reece";
  const s = STORES.get(who);
  if (!s) return;
  STORES.delete(who);
  s.retire();
}
const practiceSeen = { max: isPractice("max") };
subscribeStaffPrefs(function () {
  const now = isPractice("max");
  if (now === practiceSeen.max) return;
  practiceSeen.max = now;
  restartChatStore("max");
});
// Sign-out (and tests): stop every running answer, drop the in-memory
// threads (the next login re-reads its own from storage) and the deal tag.
export function resetLibraryChats() {
  STORES.forEach(function (s) { s.dispose(); });
  STORES.clear();
  resetDeal();
}

const EMPTY_SNAPSHOT = { staff: null, turns: [], filters: EMPTY_FILTERS, draft: "", live: null, busy: false, fresh: null };
const noSubscribe = function () { return function () {}; };
// React hook. staff null (signed out) → an empty, inert snapshot. selector
// must return a field of the snapshot or a primitive (never a new object),
// so a streaming answer only re-renders what reads `live`.
export function useLibraryChat(staff, selector) {
  const store = staff ? getChatStore(staff) : null;
  const read = store ? store.getSnapshot : function () { return EMPTY_SNAPSHOT; };
  const get = selector ? function () { return selector(read()); } : read;
  return useSyncExternalStore(store ? store.subscribe : noSubscribe, get, get);
}

// ═══════ deal basics ("Using: TX · 72 · $650K · HECM refi ✕") ═══════
// Two reporters: "file" (App — the loan file open in Loan Detail) and "call"
// (the New Call console's card; it sits on top, so it wins while open).
// `key` names the record locally (it is never sent): the chip's ✕ hides that
// RECORD's tag for the rest of the session — remembered per key, so leaving
// the file, a call opening on top of it, or opening the same file again
// never brings it back unasked; a different record shows its own chip.
// Sign-out forgets every ✕.
const US_CODES = new Set(DEAL_STATE_CODES || []);
// The property's state: its own field, else the address ("…, Phoenix AZ 85004"
// or a trailing "…, Phoenix AZ"). Only a real two-letter code counts.
export function stateFromAddress(state, address) {
  const s = String(state || "").trim().toUpperCase();
  if (US_CODES.has(s)) return s;
  const a = String(address || "");
  const m = /\b([A-Z]{2})\b\s*\d{5}(?:-\d{4})?\b/.exec(a) || /[,\s]([A-Z]{2})\s*$/.exec(a.trim());
  return m && US_CODES.has(m[1]) ? m[1] : "";
}
const numOf = function (v) { if (typeof v === "number") return v; const n = parseFloat(String(v == null ? "" : v).replace(/[$,\s]/g, "")); return Number.isFinite(n) ? n : NaN; };
// The balance goes out only when it is POSITIVE. This app stores an unknown
// balance as 0 — loanFromDb (`Number(r.mortgage_balance)||0`), the Inquiry
// form, Dashboard call-ins, prospectFromLoan, and leadBalance on a
// recorded-lien lead with no statement yet — and nothing records an explicit
// "free and clear", so a bare 0 can't be told from "not known". Sending it
// put "free & clear" on the chip and "balance $0" in the prompt, even on a
// refi (an existing reverse IS a lien), and "this deal" answers ran on a
// made-up $0 payoff. Unknown → left out, and the model is never told a number.
function fillMoney(out, hv, mb) {
  const v = numOf(hv);
  if (!(v > 0)) return;
  out.homeValue = Math.round(v);
  const b = numOf(mb);
  if (b > 0) out.mortgageBalance = Math.round(b);
}
// Refi (of an existing reverse) or first-time, in the order the rest of the
// app trusts: the lead card's explicit toggle (leadMeta.dealType — "an
// explicit pick always wins", lib.isReverseFrom), then what the last pitch
// was priced as (Calc seeds from it), then the file's own dealKind, and only
// then a guess from an existing reverse's numbers (statement PL / LOC, the
// old isReverse box). Nothing to go on → left out.
// A HomeSafe Second — FOA's 2nd lien behind a forward first mortgage that
// stays in place — is its own deal type ("second"), read the way the rest of
// the app reads it (lib.isHs2File: the card's HS2 pick, an HS2 pitch, the
// Loan Type), and before the statement guess: an HS2 file is never a refi.
function loanDealType(l, meta) {
  if (meta.dealType === "hecm") return "refi";
  if (meta.dealType === "ftr") return "first";
  if (isHs2File(l)) return "second";
  const hist = Array.isArray(l.scenarioHistory) ? l.scenarioHistory : [];
  const lastKind = hist.length && hist[hist.length - 1] ? hist[hist.length - 1].dealKind : null;
  if (lastKind === "refi" || lastKind === "first") return lastKind;
  if (l.dealKind === "refi" || l.dealKind === "first") return l.dealKind;
  const sd = l.statementData || {};
  if (meta.isReverse || meta.curPL || meta.curAvail || sd.principalLimit || sd.loc) return "refi";
  return "";
}
// A pipeline file → its deal basics (never its name, phone, email, address or id).
export function dealContextFromLoan(l) {
  if (!l || typeof l !== "object") return null;
  const meta = l.leadMeta || {};
  const out = {};
  const st = stateFromAddress(l.state || meta.state, l.prop);
  if (st) out.state = st;
  const age = loanAge(l);
  if (age > 0) out.age = age;
  fillMoney(out, l.hv, l.mb);
  const deal = l.deal && typeof l.deal === "object" ? l.deal : {};
  const product = deal.product ? String(deal.product) : "";
  const kind = loanDealType(l, meta);
  if (product) {
    out.product = (deal.lender ? String(deal.lender) + " " : "") + product;
    out.loanType = /\bHECM\b/i.test(product) ? "HECM" : kind === "second" ? "HomeSafe Second" : "Proprietary";
  } else if (l.type) out.loanType = String(l.type);
  if (kind) out.dealType = kind;
  if (l.stage) out.stage = String(l.stage);
  return cleanDealContext(out);
}
// A New Call card (prospectFromLoan / prospectFromLead shape) → its deal basics.
// The card's explicit toggle first; isReverse already applies the same order
// (lib.isReverseFrom) plus a recorded reverse lien on a list lead.
// Loan type: only a FILE's type is the deal's. A lead's loan_type is the list
// vendor's record of the EXISTING mortgage ("FHA", "New Conventional" — the
// loan being paid off, which "FHA" on the chip and in the prompt misread as
// the deal), or New Call's "HECM" stamp on every call-in; a brand-new caller
// only has blankProspect's "HECM" default. So a lead says HECM only when its
// recorded lien IS a HECM (lienIsHecm + isReverse — a HECM refi); otherwise
// nothing is sent.
export function dealContextFromProspect(p) {
  if (!p || typeof p !== "object") return null;
  const out = {};
  const st = stateFromAddress(p.state, p.address);
  if (st) out.state = st;
  const age = ageFromDobStr(p.dob) || numOf(p.age);
  if (age > 0) out.age = age;
  fillMoney(out, p.homeValue, p.mortgageBalance);
  if (p.kind === "loan") { if (p.loanType) out.loanType = String(p.loanType); }
  else if (p.kind === "lead" && p.lienIsHecm === true && p.isReverse === true) out.loanType = "HECM";
  // HS2 (HomeSafe Second — the forward first stays) is never a refi; the
  // balance on the card is that first lien, and the chat is told so.
  if (p.dealType === "hs2") out.dealType = "second";
  else if (p.dealType === "hecm") out.dealType = "refi";
  else if (p.dealType === "ftr") out.dealType = "first";
  else if (p.isReverse === true) out.dealType = "refi";
  if (p.kind === "loan" && p.stage) out.stage = String(p.stage);
  return cleanDealContext(out);
}
// "$650K", "$1.25M", "$95K"
export function fmtMoneyShort(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  if (v < 1000) return "$" + Math.round(v);
  if (v < 999500) return "$" + Math.round(v / 1000) + "K";
  const m = v / 1e6;
  return "$" + (m < 10 ? m.toFixed(2) : m.toFixed(1)).replace(/\.?0+$/, "") + "M";
}
// The chip's text — exactly what will be sent: "TX · 72 · $650K · owes $120K ·
// HECM refi" / "… · HECM first-time" (the prompt says "no existing reverse
// mortgage"). A 0 balance shows only when a caller reports one explicitly
// (the helpers above never send 0 — see fillMoney).
export function dealLabel(ctx) {
  const c = ctx || {};
  const parts = [];
  if (c.state) parts.push(c.state);
  if (c.age) parts.push(String(c.age));
  if (c.homeValue) parts.push(fmtMoneyShort(c.homeValue));
  // A HomeSafe Second: the balance is the first mortgage that STAYS (never
  // a payoff), and the deal is named as one.
  const second = c.dealType === "second";
  if (c.mortgageBalance != null) {
    if (second) parts.push(c.mortgageBalance > 0 ? "1st " + fmtMoneyShort(c.mortgageBalance) + " stays" : "no 1st mortgage");
    else parts.push(c.mortgageBalance > 0 ? "owes " + fmtMoneyShort(c.mortgageBalance) : "free & clear");
  }
  const kind = c.dealType === "refi" ? "refi" : c.dealType === "first" ? "first-time" : c.dealType === "purchase" ? "purchase" : "";
  const type = second ? "HomeSafe Second" : [c.loanType || "", kind].filter(Boolean).join(" ");
  if (type) parts.push(type);
  if (c.product) parts.push(c.product);
  if (c.stage) parts.push(c.stage);
  return parts.join(" · ");
}

const DEAL = { src: { call: null, file: null }, key: { call: "", file: "" }, activeKey: "", dismissed: new Set(), snap: null, subs: new Set() };
const DISMISSED_MAX = 500;   // records ✕'d in one session (the oldest is forgotten first)
const NO_DEAL = { ctx: null, key: "", shown: false, label: "" };
DEAL.snap = NO_DEAL;
const dealSig = function (c) { return c ? JSON.stringify(c) : ""; };
function dealRebuild() {
  const which = DEAL.src.call ? "call" : DEAL.src.file ? "file" : "";
  const ctx = which ? DEAL.src[which] : null;
  const key = which ? which + ":" + DEAL.key[which] : "";
  DEAL.activeKey = key;
  const shown = !!ctx && !DEAL.dismissed.has(key);
  const prev = DEAL.snap;
  if (prev.key === key && prev.shown === shown && dealSig(prev.ctx) === dealSig(ctx)) return;
  DEAL.snap = ctx ? { ctx: ctx, key: key, shown: shown, label: dealLabel(ctx) } : NO_DEAL;
  DEAL.subs.forEach(function (f) { try { f(); } catch (e) { /* view gone */ } });
}
// Report (or clear, with null) the deal on screen. source: "file" | "call".
export function setDealContext(ctx, source, key) {
  const s = source === "call" ? "call" : "file";
  const clean = cleanDealContext(ctx);
  const k = clean ? String(key == null || key === "" ? dealSig(clean) : key) : "";
  if (dealSig(DEAL.src[s]) === dealSig(clean) && DEAL.key[s] === k) return;
  DEAL.src[s] = clean; DEAL.key[s] = k;
  dealRebuild();
}
// The chip's ✕: not for this record, for the rest of the session (a
// different record shows its own chip).
export function dismissDealContext() {
  if (!DEAL.activeKey) return;
  DEAL.dismissed.delete(DEAL.activeKey);
  DEAL.dismissed.add(DEAL.activeKey);
  if (DEAL.dismissed.size > DISMISSED_MAX) DEAL.dismissed.delete(DEAL.dismissed.values().next().value);
  dealRebuild();
}
// {ctx, key, shown, label} — `shown` is the chip; send ctx only while it's true.
export function getDealContext() { return DEAL.snap; }
function resetDeal() {
  DEAL.src = { call: null, file: null }; DEAL.key = { call: "", file: "" }; DEAL.activeKey = ""; DEAL.dismissed = new Set();
  DEAL.snap = NO_DEAL;
  DEAL.subs.forEach(function (f) { try { f(); } catch (e) { /* view gone */ } });
}
const dealSubscribe = function (fn) { DEAL.subs.add(fn); return function () { DEAL.subs.delete(fn); }; };
export function useDealContext() { return useSyncExternalStore(dealSubscribe, getDealContext, getDealContext); }
// Reporter hook — one line in a screen: useReportDealContext(ctx|null, "call", recordKey).
// Re-reports only when the basics (or the record) change; clears on unmount.
export function useReportDealContext(ctx, source, key) {
  const clean = cleanDealContext(ctx);
  const sig = dealSig(clean);
  const k = clean ? String(key == null ? "" : key) : "";
  useEffect(function () { setDealContext(clean, source, k); }, [sig, k, source]);// eslint-disable-line react-hooks/exhaustive-deps
  useEffect(function () { return function () { setDealContext(null, source); }; }, [source]);
}
